import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Factory, 
  RefreshCw, 
  Search, 
  CheckCircle2, 
  XCircle, 
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Mail,
  X,
  Eye,
  Settings,
  GripVertical,
  Plus,
  Trash2,
  Save
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { cs } from 'date-fns/locale';

export const ProductionPage = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLogs, setImportLogs] = useState([]);
  const [lastImport, setLastImport] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [importing, setImporting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Column settings state
  const [showSettings, setShowSettings] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [userSettings, setUserSettings] = useState({
    visible_columns: [],
    column_order: [],
    conditional_formatting: []
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState(null);
  
  const canEdit = hasPagePermission(user, 'production', 'edit');

  // Load user settings on mount
  useEffect(() => {
    loadUserSettings();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, searchTerm]);

  const loadUserSettings = async () => {
    try {
      setSettingsLoading(true);
      const [settingsRes, columnsRes] = await Promise.all([
        api.get('/production/user-settings'),
        api.get('/production/available-columns')
      ]);
      
      setUserSettings(settingsRes.data);
      setAvailableColumns(columnsRes.data.columns || []);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveUserSettings = async () => {
    try {
      setSavingSettings(true);
      await api.put('/production/user-settings', userSettings);
      toast.success('Nastavení uloženo');
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Chyba při ukládání nastavení');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, lastImportRes, logsRes] = await Promise.all([
        api.get(`/production/records?page=${page}&limit=50&search=${searchTerm}`),
        api.get('/production/last-import'),
        api.get('/production/import-logs?limit=5')
      ]);
      
      setRecords(recordsRes.data.records);
      setTotalPages(recordsRes.data.pages);
      setLastImport(lastImportRes.data.last_import);
      setTotalRecords(lastImportRes.data.total_records);
      setImportLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  const handleManualImport = async () => {
    try {
      setImporting(true);
      const result = await api.post('/production/import');
      
      if (result.data.status === 'success') {
        toast.success(`Import dokončen: ${result.data.records_added} přidáno, ${result.data.records_updated} aktualizováno`);
      } else {
        toast.error(`Import selhal: ${result.data.error_message}`);
      }
      
      await fetchData();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Chyba při importu');
    } finally {
      setImporting(false);
    }
  };

  const handleRowClick = async (record) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/production/records/${record.id}`);
      setSelectedRecord(response.data);
    } catch (error) {
      console.error('Error fetching record detail:', error);
      toast.error('Chyba při načítání detailu');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedRecord(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd.MM.yyyy HH:mm', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true, locale: cs });
    } catch {
      return dateStr;
    }
  };

  // Get columns based on user settings
  const getColumns = useCallback(() => {
    if (records.length === 0) return [];
    const data = records[0].data || {};
    let allColumns = Object.keys(data).filter(col => !col.endsWith('_T'));
    
    // Apply user's column order if set
    if (userSettings.column_order && userSettings.column_order.length > 0) {
      const orderedCols = userSettings.column_order.filter(col => allColumns.includes(col));
      const remainingCols = allColumns.filter(col => !orderedCols.includes(col));
      allColumns = [...orderedCols, ...remainingCols];
    }
    
    // Apply visibility filter if set
    if (userSettings.visible_columns && userSettings.visible_columns.length > 0) {
      allColumns = allColumns.filter(col => userSettings.visible_columns.includes(col));
    }
    
    return allColumns;
  }, [records, userSettings]);

  // Check if value is an ISO date string
  const isISODateString = (value) => {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/.test(value);
  };

  // Get conditional formatting style for a cell
  const getConditionalStyle = (col, value) => {
    if (!userSettings.conditional_formatting || userSettings.conditional_formatting.length === 0) {
      return {};
    }
    
    for (const rule of userSettings.conditional_formatting) {
      if (rule.column !== col) continue;
      
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (isNaN(numValue) && rule.operator !== 'equals' && rule.operator !== 'contains') continue;
      
      let matches = false;
      const ruleValue = parseFloat(rule.value);
      
      switch (rule.operator) {
        case 'greater':
          matches = numValue > ruleValue;
          break;
        case 'less':
          matches = numValue < ruleValue;
          break;
        case 'equals':
          matches = String(value).toLowerCase() === String(rule.value).toLowerCase();
          break;
        case 'contains':
          matches = String(value).toLowerCase().includes(String(rule.value).toLowerCase());
          break;
        case 'greaterOrEqual':
          matches = numValue >= ruleValue;
          break;
        case 'lessOrEqual':
          matches = numValue <= ruleValue;
          break;
        default:
          break;
      }
      
      if (matches) {
        return {
          backgroundColor: rule.bgColor || undefined,
          color: rule.textColor || undefined,
          fontWeight: rule.bold ? 'bold' : undefined
        };
      }
    }
    
    return {};
  };

  // Format cell value
  const formatCellValue = (col, value) => {
    if (value === null || value === undefined || value === '') return '-';
    
    if (isISODateString(value)) {
      try {
        const date = new Date(value);
        return format(date, 'dd.MM.yyyy', { locale: cs });
      } catch {
        return String(value);
      }
    }
    
    const priceColumns = ['Plánovaná cena/MJ', 'Cena celk. pl.', 'Cena celk. sk.', 'Kalkulovaná cena', 'Částka dokladu'];
    const isPriceCol = priceColumns.some(p => col.toLowerCase().includes(p.toLowerCase()) || col.includes('cena') || col.includes('Cena'));
    
    if (isPriceCol && typeof value === 'number') {
      return value.toFixed(2);
    }
    
    if (col.includes('%') || col.toLowerCase().includes('plnění')) {
      if (typeof value === 'number') {
        return `${value.toFixed(1)}%`;
      }
    }
    
    if (typeof value === 'number' && !Number.isInteger(value)) {
      return value.toFixed(2);
    }
    
    const strValue = String(value);
    if (strValue.length > 50) {
      return strValue.substring(0, 47) + '...';
    }
    return strValue;
  };

  // Format value for detail view
  const formatDetailValue = (col, value) => {
    if (value === null || value === undefined || value === '') return '-';
    
    if (isISODateString(value)) {
      try {
        const date = new Date(value);
        return format(date, 'dd.MM.yyyy HH:mm', { locale: cs });
      } catch {
        return String(value);
      }
    }
    
    const priceColumns = ['Plánovaná cena/MJ', 'Cena celk. pl.', 'Cena celk. sk.', 'Kalkulovaná cena', 'Částka dokladu'];
    const isPriceCol = priceColumns.some(p => col.toLowerCase().includes(p.toLowerCase()) || col.includes('cena') || col.includes('Cena'));
    
    if (isPriceCol && typeof value === 'number') {
      return `${value.toFixed(2)} Kč`;
    }
    
    if (col.includes('%') || col.toLowerCase().includes('plnění')) {
      if (typeof value === 'number') {
        return `${value.toFixed(1)}%`;
      }
    }
    
    if (typeof value === 'number' && !Number.isInteger(value)) {
      return value.toFixed(2);
    }
    
    return String(value);
  };

  // Column visibility toggle
  const toggleColumnVisibility = (col) => {
    setUserSettings(prev => {
      const visible = prev.visible_columns || [];
      const allCols = availableColumns;
      
      if (visible.length === 0) {
        // Currently showing all - toggle means hide this one
        return {
          ...prev,
          visible_columns: allCols.filter(c => c !== col)
        };
      }
      
      if (visible.includes(col)) {
        // Remove from visible
        const newVisible = visible.filter(c => c !== col);
        // If removing last one, show all again
        return {
          ...prev,
          visible_columns: newVisible.length === 0 ? [] : newVisible
        };
      } else {
        // Add to visible
        return {
          ...prev,
          visible_columns: [...visible, col]
        };
      }
    });
  };

  // Check if column is visible
  const isColumnVisible = (col) => {
    if (!userSettings.visible_columns || userSettings.visible_columns.length === 0) {
      return true; // All visible by default
    }
    return userSettings.visible_columns.includes(col);
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e, col) => {
    setDraggedColumn(col);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetCol) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetCol) return;
    
    setUserSettings(prev => {
      let order = prev.column_order && prev.column_order.length > 0 
        ? [...prev.column_order]
        : [...availableColumns];
      
      const draggedIdx = order.indexOf(draggedColumn);
      const targetIdx = order.indexOf(targetCol);
      
      if (draggedIdx === -1 || targetIdx === -1) return prev;
      
      order.splice(draggedIdx, 1);
      order.splice(targetIdx, 0, draggedColumn);
      
      return { ...prev, column_order: order };
    });
    
    setDraggedColumn(null);
  };

  // Add new conditional formatting rule
  const addFormattingRule = () => {
    setUserSettings(prev => ({
      ...prev,
      conditional_formatting: [
        ...(prev.conditional_formatting || []),
        {
          id: Date.now(),
          column: availableColumns[0] || '',
          operator: 'greater',
          value: '',
          bgColor: '#fef2f2',
          textColor: '#dc2626',
          bold: false
        }
      ]
    }));
  };

  // Update formatting rule
  const updateFormattingRule = (id, field, value) => {
    setUserSettings(prev => ({
      ...prev,
      conditional_formatting: prev.conditional_formatting.map(rule =>
        rule.id === id ? { ...rule, [field]: value } : rule
      )
    }));
  };

  // Remove formatting rule
  const removeFormattingRule = (id) => {
    setUserSettings(prev => ({
      ...prev,
      conditional_formatting: prev.conditional_formatting.filter(rule => rule.id !== id)
    }));
  };

  // Reset settings to default
  const resetSettings = () => {
    setUserSettings({
      visible_columns: [],
      column_order: [],
      conditional_formatting: []
    });
  };

  const columns = getColumns();

  return (
    <Layout>
      <div className="p-6 max-w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Factory className="w-7 h-7 text-indigo-600" />
              Výroba
            </h1>
            <p className="text-slate-500 mt-1">Data z balírny - automatický import z emailu</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowSettings(true)}
              data-testid="column-settings-button"
            >
              <Settings className="w-4 h-4 mr-2" />
              Nastavení sloupců
            </Button>
            
            {canEdit && (
              <Button 
                onClick={handleManualImport}
                disabled={importing}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="manual-import-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${importing ? 'animate-spin' : ''}`} />
                {importing ? 'Importuji...' : 'Spustit import'}
              </Button>
            )}
          </div>
        </div>

        {/* Import Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Poslední import</CardTitle>
            </CardHeader>
            <CardContent>
              {lastImport ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {lastImport.status === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : lastImport.status === 'error' ? (
                      <XCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500 animate-spin" />
                    )}
                    <span className={`font-medium ${
                      lastImport.status === 'success' ? 'text-green-600' :
                      lastImport.status === 'error' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      {lastImport.status === 'success' ? 'Úspěšný' :
                       lastImport.status === 'error' ? 'Chyba' : 'Probíhá'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">{formatRelativeTime(lastImport.started_at)}</p>
                  <p className="text-xs text-slate-400">{formatDate(lastImport.started_at)}</p>
                </div>
              ) : (
                <p className="text-slate-400">Žádný import</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Celkem záznamů</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-indigo-500" />
                <span className="text-2xl font-bold text-slate-800">{totalRecords}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Poslední změny</CardTitle>
            </CardHeader>
            <CardContent>
              {lastImport && lastImport.status === 'success' ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Přidáno:</span>
                    <span className="font-medium text-green-600">+{lastImport.records_added}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Aktualizováno:</span>
                    <span className="font-medium text-amber-600">{lastImport.records_updated}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Beze změny:</span>
                    <span className="font-medium text-slate-600">{lastImport.records_unchanged}</span>
                  </div>
                </div>
              ) : lastImport && lastImport.status === 'error' ? (
                <div className="text-red-600 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {lastImport.error_message}
                </div>
              ) : (
                <p className="text-slate-400">-</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Import Logs */}
        {importLogs.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Historie importů</CardTitle>
              <CardDescription>Posledních 5 importů</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Čas</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-left py-2 px-3">Přidáno</th>
                      <th className="text-left py-2 px-3">Aktualizováno</th>
                      <th className="text-left py-2 px-3">Celkem</th>
                      <th className="text-left py-2 px-3">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importLogs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-3">{formatDate(log.started_at)}</td>
                        <td className="py-2 px-3">
                          {log.status === 'success' ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="w-4 h-4" /> OK
                            </span>
                          ) : log.status === 'error' ? (
                            <span className="inline-flex items-center gap-1 text-red-600" title={log.error_message}>
                              <XCircle className="w-4 h-4" /> Chyba
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-600">
                              <Clock className="w-4 h-4 animate-spin" /> Běží
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-green-600">+{log.records_added}</td>
                        <td className="py-2 px-3 text-amber-600">{log.records_updated}</td>
                        <td className="py-2 px-3">{log.total_records}</td>
                        <td className="py-2 px-3 text-xs text-slate-500 max-w-xs truncate">{log.email_subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Hledat podle čísla dokladu, názvu produktu, šarže..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="pl-10"
              data-testid="search-production"
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Vyhledávání v: Doklad, Popis, Výrobek, Šarže
          </p>
        </div>

        {/* Records Table */}
        <Card>
          <CardContent className="p-0">
            {loading || settingsLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">
                  {searchTerm ? 'Žádné záznamy nenalezeny' : 'Zatím žádná data'}
                </h3>
                <p className="text-slate-400">
                  {searchTerm ? 'Zkuste jiný vyhledávací výraz' : 'Data se importují automaticky každou hodinu'}
                </p>
              </div>
            ) : (
              <div className="overflow-auto max-h-[600px] relative">
                <table className="w-full text-sm border-collapse" data-testid="production-records-table">
                  <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                    <tr>
                      {columns.map((col) => (
                        <th 
                          key={col} 
                          className="text-left py-3 px-4 font-semibold text-slate-600 whitespace-nowrap border-b bg-slate-50"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {records.map((record) => (
                      <tr 
                        key={record.id} 
                        className="hover:bg-indigo-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(record)}
                        data-testid={`production-row-${record.id}`}
                      >
                        {columns.map((col) => {
                          const rawValue = record.data?.[col];
                          const displayValue = formatCellValue(col, rawValue);
                          const showTooltip = typeof rawValue === 'string' && rawValue.length > 50;
                          const conditionalStyle = getConditionalStyle(col, rawValue);
                          return (
                            <td 
                              key={col} 
                              className="py-3 px-4 text-slate-700 whitespace-nowrap"
                              style={conditionalStyle}
                              title={showTooltip ? rawValue : undefined}
                            >
                              {displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-slate-500" data-testid="production-pagination-info">
              Stránka {page} z {totalPages} ({(page - 1) * 50 + 1} - {Math.min(page * 50, totalRecords)} z {totalRecords} záznamů)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="production-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="production-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Detail Panel */}
        {selectedRecord && (
          <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={closeDetail}>
            <div 
              className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-800">Detail záznamu</h2>
                </div>
                <Button variant="ghost" size="icon" onClick={closeDetail} data-testid="close-detail-button">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-6">
                {detailLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Základní informace</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Doklad</p>
                            <p className="font-medium text-slate-800">{selectedRecord.doklad || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Stav</p>
                            <p className="font-medium text-slate-800">{selectedRecord.data?.['Stav'] || '-'}</p>
                          </div>
                        </div>
                        {selectedRecord.data?.['Popis'] && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Popis</p>
                            <p className="font-medium text-slate-800">{selectedRecord.data['Popis']}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Všechna pole</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="divide-y">
                          {selectedRecord.data && Object.entries(selectedRecord.data)
                            .filter(([key]) => !key.endsWith('_T'))
                            .map(([key, value]) => (
                              <div key={key} className="py-3 flex justify-between items-start gap-4">
                                <span className="text-sm text-slate-500 shrink-0">{key}</span>
                                <span className="text-sm font-medium text-slate-800 text-right break-words">
                                  {formatDetailValue(key, value)}
                                </span>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Metadata</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">ID záznamu</span>
                          <span className="font-mono text-slate-600">{selectedRecord.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Vytvořeno</span>
                          <span className="text-slate-800">{formatDate(selectedRecord.created_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Aktualizováno</span>
                          <span className="text-slate-800">{formatDate(selectedRecord.updated_at)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Column Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSettings(false)}>
            <div 
              className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-800">Nastavení zobrazení tabulky</h2>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowSettings(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Column Visibility & Order */}
                <div>
                  <h3 className="text-base font-semibold text-slate-800 mb-3">Viditelnost a pořadí sloupců</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Zaškrtněte sloupce které chcete zobrazit. Přetažením změníte pořadí.
                  </p>
                  <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                    {(userSettings.column_order && userSettings.column_order.length > 0 
                      ? userSettings.column_order 
                      : availableColumns
                    ).map((col) => (
                      <div 
                        key={col}
                        draggable
                        onDragStart={(e) => handleDragStart(e, col)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, col)}
                        className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-move ${
                          draggedColumn === col ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <GripVertical className="w-4 h-4 text-slate-400" />
                        <Checkbox 
                          id={`col-${col}`}
                          checked={isColumnVisible(col)}
                          onCheckedChange={() => toggleColumnVisibility(col)}
                        />
                        <Label htmlFor={`col-${col}`} className="flex-1 cursor-pointer">
                          {col}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Conditional Formatting */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-base font-semibold text-slate-800">Podmíněné formátování</h3>
                    <Button variant="outline" size="sm" onClick={addFormattingRule}>
                      <Plus className="w-4 h-4 mr-1" />
                      Přidat pravidlo
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">
                    Nastavte barevné zvýraznění buněk podle hodnot.
                  </p>
                  
                  {userSettings.conditional_formatting && userSettings.conditional_formatting.length > 0 ? (
                    <div className="space-y-3">
                      {userSettings.conditional_formatting.map((rule) => (
                        <div key={rule.id} className="border rounded-lg p-4 bg-slate-50">
                          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                            <div className="md:col-span-2">
                              <Label className="text-xs text-slate-500">Sloupec</Label>
                              <Select 
                                value={rule.column} 
                                onValueChange={(v) => updateFormattingRule(rule.id, 'column', v)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableColumns.map(col => (
                                    <SelectItem key={col} value={col}>{col}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label className="text-xs text-slate-500">Operátor</Label>
                              <Select 
                                value={rule.operator} 
                                onValueChange={(v) => updateFormattingRule(rule.id, 'operator', v)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="greater">větší než</SelectItem>
                                  <SelectItem value="less">menší než</SelectItem>
                                  <SelectItem value="greaterOrEqual">větší nebo rovno</SelectItem>
                                  <SelectItem value="lessOrEqual">menší nebo rovno</SelectItem>
                                  <SelectItem value="equals">rovná se</SelectItem>
                                  <SelectItem value="contains">obsahuje</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label className="text-xs text-slate-500">Hodnota</Label>
                              <Input 
                                value={rule.value}
                                onChange={(e) => updateFormattingRule(rule.id, 'value', e.target.value)}
                                className="mt-1"
                                placeholder="100"
                              />
                            </div>
                            
                            <div className="flex gap-2">
                              <div>
                                <Label className="text-xs text-slate-500">Pozadí</Label>
                                <input 
                                  type="color"
                                  value={rule.bgColor || '#fef2f2'}
                                  onChange={(e) => updateFormattingRule(rule.id, 'bgColor', e.target.value)}
                                  className="mt-1 w-full h-9 rounded cursor-pointer"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Text</Label>
                                <input 
                                  type="color"
                                  value={rule.textColor || '#dc2626'}
                                  onChange={(e) => updateFormattingRule(rule.id, 'textColor', e.target.value)}
                                  className="mt-1 w-full h-9 rounded cursor-pointer"
                                />
                              </div>
                            </div>
                            
                            <div className="flex items-end">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => removeFormattingRule(rule.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border rounded-lg bg-slate-50">
                      <p className="text-slate-400">Žádná pravidla formátování</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="border-t px-6 py-4 flex justify-between">
                <Button variant="outline" onClick={resetSettings}>
                  Obnovit výchozí
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowSettings(false)}>
                    Zrušit
                  </Button>
                  <Button 
                    onClick={saveUserSettings}
                    disabled={savingSettings}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingSettings ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Ukládám...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Uložit nastavení
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductionPage;
